/**
 * @desc The 'useBreakpoint()' hook is used to get the current
 *       screen breakpoint.
 *
 * @usage
 *    import { useBreakpoint } from "@/hooks/useBreakpoint";
 *
 *    const { isAboveSm, isBelowSm, sm } = useBreakpoint("sm");
 *    console.log({ isAboveSm, isBelowSm, sm });
 *
 *    const { isAboveMd } = useBreakpoint("md");
 *    const { isAboveLg } = useBreakpoint("lg");
 *    const { isAbove2Xl } = useBreakpoint("2xl");
 *    console.log({ isAboveMd, isAboveLg, isAbove2Xl });
 *
 * @see https://stackoverflow.com/a/76630444/6543935
 * @requirements npm install react-responsive
 */
import { useMediaQuery } from 'react-responsive';

const breakpoints = {
  shortScreen: '679px',
} as const;

export function useHeightBreakpoint<K extends keyof typeof breakpoints>(
  breakpointKey: K,
) {
  const breakpointValue = breakpoints[breakpointKey];
  const bool = useMediaQuery({
    maxHeight: breakpointValue,
    minWidth: '480px',
  });
  const capitalizedKey =
    breakpointKey[0]!.toUpperCase() + breakpointKey.substring(1);

  type KeyAbove = `isAbove${Capitalize<K>}`;
  type KeyBelow = `isBelow${Capitalize<K>}`;

  return {
    [breakpointKey]: Number(String(breakpointValue).replace(/[^0-9]/g, '')),
    [`isAbove${capitalizedKey}`]: !bool,
    [`isBelow${capitalizedKey}`]: bool,
  } as Record<K, number> & Record<KeyAbove | KeyBelow, boolean>;
}
